import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Stack } from "@chakra-ui/react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";
import { AuthShell, Field, inputStyle, ErrorNote, SuccessNote, PasswordInput, PasswordRequirements, PasswordStrength } from "@/pages/auth/AuthShell";
import { checkPassword, interpretPasswordError } from "@/lib/passwordPolicy";

export default function ResetPassword() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && s)) setReady(true);
      // The form must never promise an action it cannot perform: if the recovery
      // session ends (sign-out, expiry), fall back to the request-a-new-link note.
      if (event === "SIGNED_OUT" || !s) setReady(false);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!checkPassword(password).valid) {
      setError("Please choose a password that meets all the requirements below.");
      return;
    }
    setLoading(true);
    try {
      // Verify a live session exists before promising the update.
      const { data } = await supabase.auth.getSession();
      if (!data?.session) {
        setReady(false);
        return;
      }
      await updatePassword(password);
      supabase.rpc("log_account_event", { p_event: "password_changed" }).then(() => {});
      setDone(true);
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      // Never surface raw internal strings ("Auth session missing!") — plain
      // English plus a route forward.
      if (/session missing/i.test(String(err?.message || ""))) {
        setReady(false);
      } else {
        setError(interpretPasswordError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong new password for your account.">
      {done ? (
        <SuccessNote>Password updated. Redirecting you to the app…</SuccessNote>
      ) : !ready ? (
        <ErrorNote data-testid="reset-link-invalid-note">
          This reset link is no longer valid — it may have expired or already been used.
          Please request a new one from the "Forgot password" page and use it soon after it arrives.
        </ErrorNote>
      ) : (
        <form onSubmit={submit}>
          <Stack gap="4">
            <ErrorNote>{error}</ErrorNote>
            <Field label="New password">
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} data-testid="reset-password" />
              <PasswordStrength value={password} />
              <PasswordRequirements value={password} />
            </Field>
            <Button type="submit" w="full" loading={loading} data-testid="reset-submit">
              Update password
            </Button>
          </Stack>
        </form>
      )}
    </AuthShell>
  );
}
